<script>
import Layout from "@/router/layouts/main";
import { api } from "@/state/services";
import Calendar from "primevue/calendar";
import AdesaoGateModal from "./components/AdesaoGateModal.vue";

export default {
  components: { Layout, Calendar, AdesaoGateModal },
  data() {
    return {
      loading: true,
      periodMonth: new Date(),
      // closed/closed_at vêm da API: a partir do dia 5 do mês seguinte o
      // período é fechado e o resultado para de ser recalculado.
      data: { month: null, year: null, prizes: [], ranking: [], closed: false, closed_at: null },
    };
  },
  computed: {
    top3() {
      return this.data.ranking ? this.data.ranking.slice(0, 3) : [];
    },
  },
  watch: {
    periodMonth() {
      this.load();
    },
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      const d = this.periodMonth || new Date();
      const month = d.getMonth() + 1;
      const year = d.getFullYear();

      api.get(`afiliados/ranking/geral?month=${month}&year=${year}`)
        .then((r) => {
          if (r.data.status === "success") {
            this.data = r.data;
          }
        })
        .finally(() => { this.loading = false; });
    },
    showPeriod() {
      const d = this.periodMonth || new Date();
      return [String(d.getMonth() + 1).padStart(2, "0"), d.getFullYear()].join("/");
    },
    nameInitial(name) {
      return name ? name.trim().charAt(0).toUpperCase() : "?";
    },
    formatMoney(v) {
      return parseFloat(v || 0).toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    // A API manda 'YYYY-MM-DD HH:MM:SS' (horário de Brasília, gravado pelo PHP).
    // O construtor Date do Safari não aceita esse formato com espaço, então a
    // data é montada na mão em vez de parseada.
    formatClosedAt(value) {
      if (!value) return "";
      const [date] = String(value).split(" ");
      const [y, m, d] = date.split("-");
      return d && m && y ? `${d}/${m}/${y}` : "";
    },
  },
};
</script>

<template>
  <Layout>
    <AdesaoGateModal />
    <div class="page-title-box mb-3">
      <h3>Ranking de Afiliados</h3>
    </div>

    <div class="mb-4" style="max-width: 250px;">
      <!-- Sem rótulo visível, o input do Calendar era anunciado só como
           "caixa de texto" pelo leitor de tela. -->
      <Calendar
        v-model="periodMonth"
        view="month"
        dateFormat="mm/yy"
        aria-label="Mês de referência"
      />
    </div>

    <div v-if="loading" class="text-center py-4">
      <b-spinner variant="primary"></b-spinner>
    </div>

    <template v-else>
      <!-- Estado do período. Um mês fechado não muda mais; o mês em andamento é
           uma parcial. Sem essa distinção, quem confere a premiação não sabe se
           a colocação na tela é definitiva. -->
      <div
        v-if="data.ranking && data.ranking.length"
        class="alert py-2 px-3 small d-flex align-items-center mb-3"
        :class="data.closed ? 'alert-success' : 'alert-light border'"
      >
        <i class="mr-2" :class="data.closed ? 'bx bx-lock-alt' : 'bx bx-time-five'"></i>
        <span v-if="data.closed">
          <strong>Resultado final</strong> de {{ showPeriod() }}
          <template v-if="data.closed_at">— fechado em {{ formatClosedAt(data.closed_at) }}</template>.
          As colocações deste mês não mudam mais.
        </span>
        <span v-else>
          <strong>Parcial em tempo real.</strong> As colocações de {{ showPeriod() }} ainda podem mudar
          até o fechamento, no dia 5 do mês seguinte.
        </span>
      </div>

      <!-- Pódio Top 3 -->
      <div v-if="top3.length" class="card border-0 shadow-sm mb-3">
        <div class="card-body pb-2">
          <div class="text-center mb-3">
            <h6 class="font-weight-bold text-dark mb-0" style="text-transform:uppercase;letter-spacing:.08em;">
              🏆 Top 3 Premiados — {{ showPeriod() }}
            </h6>
          </div>
          <div class="podium d-flex justify-content-center align-items-end" style="gap:1rem;">

            <div v-if="top3[1]" class="podium-item text-center" style="flex:1;max-width:140px;">
              <div class="podium-avatar silver-aura">
                <span class="podium-number">2°</span>
                <img v-if="top3[1].avatar" :src="top3[1].avatar" class="podium-photo" :alt="top3[1].name" />
                <div v-else class="podium-initial">{{ nameInitial(top3[1].name) }}</div>
              </div>
              <div class="podium-name text-truncate">{{ top3[1].name }}</div>
              <div class="small text-muted">{{ top3[1].points }} pontos</div>
              <div v-if="top3[1].prize" class="prize-badge silver-prize mt-1">R$ {{ formatMoney(top3[1].prize.value) }}</div>
              <div class="podium-base silver-base" style="height:60px;"></div>
            </div>

            <div v-if="top3[0]" class="podium-item text-center" style="flex:1;max-width:140px;">
              <div class="crown-icon">👑</div>
              <div class="podium-avatar gold-aura">
                <span class="podium-number">1°</span>
                <img v-if="top3[0].avatar" :src="top3[0].avatar" class="podium-photo" :alt="top3[0].name" />
                <div v-else class="podium-initial">{{ nameInitial(top3[0].name) }}</div>
              </div>
              <div class="podium-name text-truncate">{{ top3[0].name }}</div>
              <div class="small text-muted">{{ top3[0].points }} pontos</div>
              <div v-if="top3[0].prize" class="prize-badge gold-prize mt-1">R$ {{ formatMoney(top3[0].prize.value) }}</div>
              <div class="podium-base gold-base" style="height:90px;"></div>
            </div>

            <div v-if="top3[2]" class="podium-item text-center" style="flex:1;max-width:140px;">
              <div class="podium-avatar bronze-aura">
                <span class="podium-number">3°</span>
                <img v-if="top3[2].avatar" :src="top3[2].avatar" class="podium-photo" :alt="top3[2].name" />
                <div v-else class="podium-initial">{{ nameInitial(top3[2].name) }}</div>
              </div>
              <div class="podium-name text-truncate">{{ top3[2].name }}</div>
              <div class="small text-muted">{{ top3[2].points }} pontos</div>
              <div v-if="top3[2].prize" class="prize-badge bronze-prize mt-1">R$ {{ formatMoney(top3[2].prize.value) }}</div>
              <div class="podium-base bronze-base" style="height:45px;"></div>
            </div>

          </div>
        </div>
      </div>

      <!-- Lista completa -->
      <div v-if="data.ranking && data.ranking.length" class="card border-0 shadow-sm">
        <div class="card-header bg-white border-bottom py-2">
          <h6 class="mb-0 font-weight-bold text-dark small" style="text-transform:uppercase;letter-spacing:.06em;">
            Ranking completo — {{ data.ranking.length }} participante{{ data.ranking.length !== 1 ? 's' : '' }}
          </h6>
        </div>
        <div class="table-responsive">
          <!-- `table-cards`: empilha cada posicao do ranking como card abaixo de md. -->
          <table class="table table-hover mb-0 small table-cards">
            <thead class="thead-light">
              <tr>
                <th style="width:50px;">#</th>
                <th>Nome</th>
                <th>Responsável</th>
                <th>Regional</th>
                <th class="text-right">Pontos</th>
                <th class="text-right">Prêmio</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in data.ranking" :key="item.position">
                <td class="align-middle font-weight-bold" data-label="Posição">
                  <span v-if="item.position === 1" class="text-warning">🥇</span>
                  <span v-else-if="item.position === 2" class="text-secondary">🥈</span>
                  <span v-else-if="item.position === 3" style="color:#cd7f32;">🥉</span>
                  <span v-else class="text-muted">{{ item.position }}°</span>
                </td>
                <td class="align-middle" data-label="Nome">
                  <div class="d-flex align-items-center" style="gap:.5rem;">
                    <div class="list-avatar">
                      <img v-if="item.avatar" :src="item.avatar" class="list-avatar-img rounded-circle" :alt="item.name" />
                      <div v-else class="list-avatar-initial rounded-circle">{{ nameInitial(item.name) }}</div>
                    </div>
                    <span>{{ item.name }}</span>
                  </div>
                </td>
                <td class="align-middle text-muted small" data-label="Responsável">{{ item.responsavel || '—' }}</td>
                <td class="align-middle text-muted small" data-label="Regional">{{ item.regional || '—' }}</td>
                <td class="align-middle text-right font-weight-bold" data-label="Pontos">{{ item.points }}</td>
                <td class="align-middle text-right" data-label="Prêmio">
                  <span v-if="item.prize" class="text-success font-weight-bold">R$ {{ formatMoney(item.prize.value) }}</span>
                  <span v-else class="text-muted">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Estado vazio -->
      <div v-else class="card border-0 shadow-sm">
        <div class="card-body text-center py-5 text-muted">
          <i class="mdi mdi-trophy-outline" style="font-size:2.5rem;opacity:.3;"></i>
          <p class="mt-2 mb-0 small">Nenhum lead convertido neste período.</p>
        </div>
      </div>
    </template>
  </Layout>
</template>

<style scoped>
.podium { flex-wrap: nowrap; align-items: flex-end; }
.podium-item { position: relative; }
.crown-icon { font-size: 1.4rem; margin-bottom: 2px; }

.podium-avatar {
  width: 68px; height: 68px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; flex-direction: column;
  margin: 0 auto 4px; position: relative; background: var(--st-canvas);
}
.podium-number {
  font-size: .6rem; font-weight: 700; color: var(--st-text-inverse); background: rgba(0,0,0,.35);
  border-radius: 99px; padding: 1px 5px; position: absolute; bottom: -2px; right: -2px; line-height: 1.4;
}
.podium-initial { font-size: 1.4rem; font-weight: 700; color: var(--st-text); }
.podium-photo { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; display: block; }
.podium-name { font-size: .75rem; max-width: 120px; margin: 0 auto; }
.prize-badge { display: inline-block; font-size: .7rem; font-weight: 700; border-radius: 99px; padding: 2px 8px; }

.gold-aura   { box-shadow: 0 0 0 3px #ffd700, 0 0 18px 6px rgba(255,215,0,.45); }
.silver-aura { box-shadow: 0 0 0 3px #c0c0c0, 0 0 14px 5px rgba(192,192,192,.4); }
.bronze-aura { box-shadow: 0 0 0 3px #cd7f32, 0 0 14px 5px rgba(205,127,50,.4); }

.gold-prize   { background: #fff3cd; color: #856404; }
.silver-prize { background: #f0f0f0; color: #555; }
.bronze-prize { background: #fce8d5; color: #7d4e1e; }

.gold-base   { background: linear-gradient(to top, #f5c518, #ffe066); border-radius: 4px 4px 0 0; }
.silver-base { background: linear-gradient(to top, #b0b0b0, #d8d8d8); border-radius: 4px 4px 0 0; }
.bronze-base { background: linear-gradient(to top, #a0522d, #cd8b52); border-radius: 4px 4px 0 0; }

.list-avatar { flex-shrink: 0; }
.list-avatar-img { width: 28px; height: 28px; object-fit: cover; }
.list-avatar-initial {
  width: 28px; height: 28px; background: var(--st-brand); color: var(--st-text-inverse); font-size: .7rem; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
</style>
